/**
 * 관리자 › NAS 연동 메일 업로드.
 *
 * 한 화면에서 전 과정을 본다:
 *   ① 연결 상태  — DSM 접속·계정·허용 루트
 *   ② 위치 선택  — NAS 최상위(/)부터 펼치는 폴더 트리
 *                 (경로 직접 입력 X. '⭐️' 같은 이모지·변형선택자가 섞여 있어 타이핑이 불가능하다)
 *   ③ 사용자 목록 조회 — ZIP 을 열어 계정·메일함별 건수와 직원 매칭을 미리 보여 준다
 *   ④ 실행 + 진행 — 진행률·현재 처리 중·사람별 결과·실패 사유
 *
 * 왜 ③ 이 필수인가: 수만 통을 남의 편지함에 넣는 작업이라 계정 하나만 어긋나도 복구가 사실상
 * 불가능하다. 그래서 매칭이 전부 확정되기 전에는 실행 버튼이 열리지 않는다(서버도 같은 검사를 한다).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Checkbox, Chip, CircularProgress, Divider, IconButton, LinearProgress,
    Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import FolderRoundedIcon        from "@mui/icons-material/FolderRounded";
import ArticleRoundedIcon       from "@mui/icons-material/ArticleRounded";
import ChevronRightRoundedIcon  from "@mui/icons-material/ChevronRightRounded";
import RefreshRoundedIcon       from "@mui/icons-material/RefreshRounded";
import CheckCircleRoundedIcon   from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon         from "@mui/icons-material/ErrorRounded";
import WarningAmberRoundedIcon  from "@mui/icons-material/WarningAmberRounded";
import StorageRoundedIcon       from "@mui/icons-material/StorageRounded";

import ActionButton from "../../component/common/ActionButton";
import { ds }       from "../../styles/designSystem";
import { notify }   from "../../util/toast";
import { confirm }  from "../../util/confirmDialog";
import { archiveApi } from "./api/archiveApi";

/** 트리의 뿌리. 서버는 이 경로에 대해 공유폴더 목록을 돌려준다(FileStation list_share). */
const ROOT = "/";

/**
 * 조상 중 선택된 폴더가 있는가 — 있으면 이 폴더는 <b>이미 포함</b>된 것이다.
 *
 * <p>상위 폴더를 고르면 서버의 findZips 가 그 아래를 훑으므로 하위는 자동으로 포함된다.
 * 그런데 화면에서 하위 체크박스가 비어 있으면 "안 들어가나?" 로 읽힌다. 그래서 조상이 선택되면
 * 하위도 <b>체크된 것으로 그리고 잠근다</b> — 실제 동작과 화면을 일치시킨다.
 *
 * <p>하위를 일일이 켜서 보내지 않는 이유: 서버가 어차피 ZIP 경로로 중복을 없애므로 불필요하고,
 * 지연 로딩이라 <b>아직 안 펼친 하위는 목록에 있지도 않다</b>(그걸 다 받아오려면 폴더마다 요청이 터진다).
 */
const coveredByAncestor = (path, selected) => {
    for (const s of selected) {
        if (path !== s && path.startsWith(s.endsWith("/") ? s : s + "/")) return true;
    }
    return false;
};

/**
 * 이 ZIP 을 실행해도 되는가 — <b>모든 계정</b>에 주인이 있어야 한다.
 *
 * <p>서버({@code PersonPlan.matched()})와 같은 규칙이어야 한다. 예전처럼 대표 계정 하나만 보면,
 * 계정이 여럿인 공용 ZIP 에서 버튼은 초록인데 서버가 409 로 막는 상태가 된다.
 */
const isPlanMatched = (p) =>
    p.accounts?.length ? p.accounts.every(a => a.employeeId) : !!p.employeeId;

const fmtBytes = (n) => {
    if (!n) return "0 B";
    const u = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(n) / Math.log(1024));
    return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtNum = (n) => (n ?? 0).toLocaleString();
const fmtEta = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${s}초`;
    if (s < 3600) return `${Math.round(s / 60)}분`;
    return `${(s / 3600).toFixed(1)}시간`;
};

/* ─── 섹션 카드 ─────────────────────────────────────────── */
const Card = ({ title, step, right, children }) => (
    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25,
                   borderBottom: "1px solid #f1f5f9" }}>
            {step != null && (
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                           bgcolor: ds.colors.primary, color: "#fff", fontSize: 12, fontWeight: 700,
                           display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {step}
                </Box>
            )}
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: "#1e293b", flex: 1 }}>{title}</Typography>
            {right}
        </Box>
        <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
);

export default function MailArchivePage() {
    const [available, setAvailable] = useState(null);   // null=확인중
    const [availInfo, setAvailInfo] = useState(null);  // 안 될 때의 사유(서버가 알려준다)
    const [conn, setConn]           = useState(null);
    const [connLoading, setConnLoading] = useState(false);

    /* 트리 상태.
       childrenMap 은 '펼쳐 본 경로'만 담는다 — 아카이브 아래엔 ZIP 이 수백 개라 전체를 미리
       받으면 첫 화면이 수 초간 멈춘다. requestedRef 는 같은 경로를 두 번 조회하지 않기 위한
       표식이며, state 로 두면 loadChildren 이 매번 새로 만들어져 useEffect 가 반복 발화한다. */
    /* 선택한 폴더들 = ③ 조회 대상. 부서 여러 개를 한 번에 돌리는 게 기본 사용법이라 집합으로 둔다.
       부모·자식을 같이 골라도 서버가 ZIP 경로로 중복을 없애므로 화면에서 막지 않는다. */
    const [selectedPaths, setSelectedPaths] = useState(() => new Set());
    const [childrenMap, setChildrenMap]   = useState({});     // { 경로: BrowseItem[] }
    const [expanded, setExpanded]         = useState(() => new Set());
    const [loadingPaths, setLoadingPaths] = useState(() => new Set());
    const requestedRef = useRef(new Set());

    const [plan, setPlan]       = useState(null);
    const [scanning, setScanning] = useState(false);
    /* 이관할 ZIP 선택 — zipName 집합. 조회할 때마다 전체 선택으로 초기화한다.
       (기본이 '전체' 여야 한다. 기본을 빈 선택으로 두면 조회 직후 실행 버튼이 죽어 있어
        "왜 안 눌리지" 로 헤매게 된다 — 체크박스는 <b>빼는</b> 용도다.) */
    const [selectedZips, setSelectedZips] = useState(() => new Set());

    const [job, setJob]         = useState(null);
    const [starting, setStarting] = useState(false);
    const pollRef = useRef(null);

    /* ── 진입 시 권한 확인 ── */
    useEffect(() => {
        archiveApi.available()
            .then(r => { setAvailable(!!r?.available); setAvailInfo(r); })
            .catch(() => setAvailable(false));
    }, []);

    const loadConnection = useCallback(async () => {
        setConnLoading(true);
        try { setConn(await archiveApi.connection()); }
        catch (e) { notify(e?.response?.data?.message || "연결 확인 실패", "error"); }
        finally { setConnLoading(false); }
    }, []);

    /** 한 경로의 자식만 조회해 캐시에 넣는다. 이미 받아 온 곳은 다시 부르지 않는다. */
    const loadChildren = useCallback(async (p, force = false) => {
        if (!force && requestedRef.current.has(p)) return;
        requestedRef.current.add(p);
        setLoadingPaths(s => new Set(s).add(p));
        try {
            const list = await archiveApi.browse(p);
            setChildrenMap(c => ({ ...c, [p]: list || [] }));
        } catch (e) {
            // 실패한 경로는 표식을 지워 다시 눌러볼 수 있게 하고, 열린 것처럼 보이지 않도록 접는다.
            requestedRef.current.delete(p);
            setExpanded(s => { const n = new Set(s); n.delete(p); return n; });
            notify(e?.response?.data?.message || "폴더 조회 실패", "error");
        } finally {
            setLoadingPaths(s => { const n = new Set(s); n.delete(p); return n; });
        }
    }, []);

    const toggleNode = useCallback((p) => {
        const willOpen = !expanded.has(p);
        setExpanded(s => { const n = new Set(s); if (willOpen) n.add(p); else n.delete(p); return n; });
        if (willOpen) loadChildren(p);
    }, [expanded, loadChildren]);

    /** 트리 전체 새로고침 — NAS 에서 폴더가 바뀌었을 때 쓴다. 선택은 유지한다. */
    const reloadTree = useCallback(() => {
        requestedRef.current = new Set();
        setChildrenMap({});
        setExpanded(new Set());
        loadChildren(ROOT, true);
    }, [loadChildren]);

    useEffect(() => { if (available) loadConnection(); }, [available, loadConnection]);
    useEffect(() => { if (conn?.ok) loadChildren(ROOT); }, [conn?.ok, loadChildren]);

    /* ── 진행 상황 폴링 — 실행 중일 때만 ── */
    useEffect(() => {
        const stop = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
        if (!job?.running) { stop(); return stop; }
        pollRef.current = setInterval(async () => {
            try {
                const s = await archiveApi.status(job.jobId);
                setJob(s);
                if (!s?.running) stop();
            } catch { /* 일시적 실패는 무시 — 다음 주기에 다시 물어본다 */ }
        }, 2000);
        return stop;
    }, [job?.running, job?.jobId]);

    /* 새로고침 후에도 실행 중인 작업이 있으면 이어서 보여 준다 */
    useEffect(() => {
        if (!available) return;
        archiveApi.status().then(s => { if (s?.jobId) setJob(s); }).catch(() => {});
    }, [available]);

    /**
     * 선택된 ZIP 기준 집계 — 확인 문구·실행 가능 판정이 전부 이 값을 본다.
     *
     * <p>전체(plan) 수치와 분리해 두는 게 핵심이다. 서버도 선택분만 검증하므로
     * (매칭 실패한 사람을 체크 해제하면 나머지는 그대로 진행된다) 화면 판정이 전체 기준이면
     * 서버는 되는데 버튼만 죽어 있는 상태가 된다.
     */
    const selection = useMemo(() => {
        const people = (plan?.people || []).filter(p => selectedZips.has(p.zipName));
        const sumIf = (pick) => people.reduce(
            (a, p) => a + p.mailboxes.reduce((b, m) => b + (pick(m) ? m.count : 0), 0), 0);
        return {
            people,
            mails:       people.reduce((a, p) => a + p.emlCount, 0),
            systemMails: sumIf(m => !m.custom),
            customMails: sumIf(m =>  m.custom),
            unmatched:   people.filter(p => !isPlanMatched(p)).length,
        };
    }, [plan, selectedZips]);

    const toggleZip = useCallback((zipName) => {
        setSelectedZips(s => {
            const n = new Set(s);
            if (n.has(zipName)) n.delete(zipName); else n.add(zipName);
            return n;
        });
    }, []);

    const togglePath = useCallback((p) => {
        setSelectedPaths(s => {
            const n = new Set(s);
            if (n.has(p)) { n.delete(p); return n; }
            // 상위를 새로 고르면 그 아래 개별 선택은 지운다 — 이제 상위에 포함되므로 중복이고,
            // 남겨 두면 서버로 보내는 목록만 길어진다(결과는 같지만 로그·확인문구가 지저분해진다).
            const prefix = p.endsWith("/") ? p : p + "/";
            for (const x of [...n]) if (x.startsWith(prefix)) n.delete(x);
            n.add(p);
            return n;
        });
    }, []);

    /** 헤더 체크박스 — 하나라도 빠져 있으면 전체 선택, 전부 켜져 있으면 전체 해제. */
    const toggleAllZips = useCallback(() => {
        const all = (plan?.people || []).map(p => p.zipName);
        setSelectedZips(s => s.size === all.length ? new Set() : new Set(all));
    }, [plan]);

    const runScan = async () => {
        setScanning(true); setPlan(null);
        try {
            const p = await archiveApi.preview([...selectedPaths]);
            setPlan(p);
            setSelectedZips(new Set((p.people || []).map(x => x.zipName)));
            notify(`ZIP ${p.zipCount}개 · 메일 ${fmtNum(p.emlCount)}건 조회 완료`, "success");
        } catch (e) {
            notify(e?.response?.data?.message || "조회 실패", "error");
        } finally { setScanning(false); }
    };

    const runImport = async () => {
        // 빈 선택으로는 절대 보내지 않는다 — 서버는 zipNames 가 비면 '필터 없음'(=전원)으로 해석한다.
        // 버튼이 이미 막고 있지만, 여기서 한 번 더 막아야 그 해석 차이가 사고로 이어지지 않는다.
        if (!plan || selection.people.length === 0) return;
        // 확인 문구는 <b>고른 것</b> 기준이어야 한다. 전체 수치를 보여 주면 몇 명을 빼 놓고도
        // 전부 들어가는 줄 알고 승인하게 된다.
        const sel = selection;
        const skipped = plan.people.length - sel.people.length;
        const ok = await confirm({
            message: `${sel.people.length}명 · 메일 ${fmtNum(sel.mails)}건을 이관합니다.\n\n`
                   + `· 시스템 폴더 ${fmtNum(sel.systemMails)}건\n`
                   + `· 내 폴더(자동 생성) ${fmtNum(sel.customMails)}건\n`
                   + (skipped > 0 ? `· 선택 해제로 제외 ${skipped}명\n` : "")
                   + `\n이미 넣은 메일은 자동으로 건너뜁니다. 계속할까요?`,
        });
        if (!ok) return;
        setStarting(true);
        try {
            const s = await archiveApi.start({ paths: [...selectedPaths], zipNames: [...selectedZips] });
            setJob(s);
            notify("이관을 시작했습니다.", "success");
        } catch (e) {
            notify(e?.response?.data?.message || "시작 실패", "error");
        } finally { setStarting(false); }
    };

    const cancelImport = async () => {
        if (!(await confirm({ message: "이관을 중단할까요?\n이미 들어간 메일은 그대로 남습니다." }))) return;
        try { await archiveApi.cancel(job?.jobId); notify("중단을 요청했습니다.", "info"); }
        catch { notify("중단 요청 실패", "error"); }
    };

    /* ── 권한 없음 ── */
    if (available === null) {
        return <Box sx={{ p: 6, textAlign: "center" }}><CircularProgress size={28} /></Box>;
    }
    if (!available) {
        return (
            <Box sx={{ p: 6, textAlign: "center", color: "#64748b" }}>
                <StorageRoundedIcon sx={{ fontSize: 44, color: "#cbd5e1", mb: 1 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>접근 권한이 없습니다</Typography>
                <Typography sx={{ fontSize: 13, mb: 1 }}>이 기능은 이관 담당자만 사용할 수 있습니다.</Typography>
                {/* 사유를 그대로 보여 준다 — 없으면 왜 안 되는지 알 길이 없어 서버 로그를 뒤져야 한다 */}
                {availInfo?.reasons?.map((r, i) => (
                    <Typography key={i} sx={{ fontSize: 12.5, color: "#b45309" }}>· {r}</Typography>
                ))}
            </Box>
        );
    }

    const canStart = plan && selection.people.length > 0 && selection.unmatched === 0 && !job?.running;

    return (
        <Box sx={{ p: 2.5, maxWidth: 1180, mx: "auto" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#1e293b", mb: 0.5 }}>
                NAS 연동 메일 업로드
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2.5 }}>
                Synology NAS 에 보관된 옛 메일 백업(.zip)을 시스템으로 옮깁니다.
                압축을 풀지 않고 필요한 부분만 읽으며, 이미 넣은 메일은 다시 넣지 않습니다.
            </Typography>

            {/* ① 연결 */}
            <Card title="연결 상태" step={1}
                  right={
                      <Tooltip title="다시 확인">
                          <IconButton size="small" onClick={loadConnection} disabled={connLoading}>
                              <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                      </Tooltip>
                  }>
                {connLoading && !conn ? <LinearProgress /> : conn ? (
                    <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="center">
                        <Chip
                            size="small"
                            icon={conn.ok ? <CheckCircleRoundedIcon /> : <ErrorRoundedIcon />}
                            label={conn.ok ? `연결됨 (${conn.elapsedMs}ms)` : "연결 실패"}
                            color={conn.ok ? "success" : "error"}
                            variant={conn.ok ? "filled" : "outlined"}
                        />
                        <Kv label="주소"       value={conn.baseUrl} />
                        <Kv label="계정"       value={conn.username} />
                        <Kv label="허용 경로"
                            value={conn.allowedRoot === "/" ? "제한 없음 (전체 탐색)" : conn.allowedRoot} />
                        <Kv label="공유폴더"   value={`${conn.shareCount}개`} />
                        {conn.error && (
                            <Typography sx={{ fontSize: 12.5, color: "#dc2626" }}>{conn.error}</Typography>
                        )}
                    </Stack>
                ) : null}
            </Card>

            {/* ② 위치 선택 — NAS 최상위부터 펼치는 트리 */}
            <Card title="파일 위치 선택" step={2}
                  right={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography noWrap sx={{ fontSize: 12, maxWidth: 460,
                                                   color: selectedPaths.size ? "#1e293b" : "#94a3b8" }}>
                              {selectedPaths.size === 0
                                  ? "폴더를 고르세요 (여러 개 선택 가능)"
                                  : selectedPaths.size === 1
                                      ? [...selectedPaths][0]
                                      : `${selectedPaths.size}개 폴더 선택 — ` +
                                        [...selectedPaths].map(p => p.split("/").pop()).join(", ")}
                          </Typography>
                          <Tooltip title="트리 새로고침">
                              <IconButton size="small" onClick={reloadTree}>
                                  <RefreshRoundedIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                          </Tooltip>
                      </Stack>
                  }>
                <Box sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid #f1f5f9",
                           borderRadius: 1, py: 0.5 }}>
                    {!childrenMap[ROOT] && <LinearProgress />}
                    {childrenMap[ROOT]?.length === 0 && (
                        <Typography sx={{ fontSize: 12.5, color: "#cbd5e1", p: 1.5 }}>
                            공유폴더가 없습니다. 계정 권한을 확인하세요.
                        </Typography>
                    )}
                    {childrenMap[ROOT]?.map(it => (
                        <TreeNode
                            key={it.path} item={it} depth={0}
                            childrenMap={childrenMap} expanded={expanded} loadingPaths={loadingPaths}
                            selected={selectedPaths} onToggle={toggleNode} onSelect={togglePath}
                        />
                    ))}
                </Box>
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.75 }}>
                    화살표로 펼치고 <b>체크박스로 고릅니다 — 여러 폴더를 함께 고를 수 있습니다</b>
                    (예: 기획1팀 + 기획2팀). NAS 최상위(/)부터 전부 탐색할 수 있습니다.
                </Typography>
            </Card>

            {/* ③ 사용자 목록 조회 */}
            <Card title="사용자 목록 조회" step={3}
                  right={
                      <ActionButton
                          variant="primary" label={scanning ? "조회 중…" : "조회"}
                          onClick={runScan} disabled={scanning || selectedPaths.size === 0}
                          loading={scanning}
                          sx={{ py: 0.4, fontSize: 12.5 }}
                      />
                  }>
                {scanning && <LinearProgress sx={{ mb: 1.5 }} />}
                {!plan && !scanning && (
                    <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                        위에서 아카이브 폴더를 고른 뒤 조회를 누르세요. 압축을 풀지 않고 목록만 읽어 수십 초면 끝납니다.
                    </Typography>
                )}
                {plan && <PlanView plan={plan} selected={selectedZips}
                                   onToggle={toggleZip} onToggleAll={toggleAllZips} />}
            </Card>

            {/* ④ 실행 · 진행 */}
            <Card title="이관 실행" step={4}
                  right={
                      job?.running
                          ? <ActionButton variant="secondary" label="중단" onClick={cancelImport}
                                          sx={{ py: 0.4, fontSize: 12.5, color: "#dc2626", borderColor: "#fecaca" }} />
                          : <ActionButton variant="primary" label="이관 시작" onClick={runImport}
                                          disabled={!canStart || starting} loading={starting}
                                          sx={{ py: 0.4, fontSize: 12.5 }} />
                  }>
                {/* 왜 못 누르는지를 정확히 짚어 준다 — 버튼만 죽어 있으면 조회를 다시 하며 헤맨다. */}
                {!job && !canStart && (
                    <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>
                        {!plan
                            ? "먼저 사용자 목록을 조회하세요."
                            : selection.people.length === 0
                                ? "이관할 대상이 하나도 선택되지 않았습니다. 표에서 체크하세요."
                                : `선택한 대상 중 직원 매칭이 안 된 사람이 ${selection.unmatched}명 있습니다. `
                                  + `매칭을 지정하거나 그 줄의 체크를 해제하면 나머지만 이관할 수 있습니다.`}
                    </Typography>
                )}
                {job && <JobView job={job} />}
            </Card>
        </Box>
    );
}

/* ─── 폴더 트리 ─────────────────────────────────────────── */

/**
 * 트리 한 줄과 그 아래 가지.
 *
 * <p>폴더는 <b>화살표=펼치기 / 이름=고르기</b> 로 나눠 뒀다. 한 곳에 두 동작을 겹치면
 * "열어 보려고 눌렀는데 선택이 바뀌는" 일이 생겨, 고른 뒤 안쪽을 확인하려다 선택을 잃는다.
 *
 * <p>파일도 함께 그리되 고를 수는 없다 — ③ 조회는 폴더를 받아 그 안의 ZIP 을 훑는 동작이라
 * 파일을 넘겨도 할 일이 없다. 그래도 보여 주는 건 "이 폴더가 맞나" 를 눈으로 확인하기 위해서다.
 */
function TreeNode({ item, depth, childrenMap, expanded, loadingPaths, selected, onToggle, onSelect }) {
    const isDir    = item.dir;
    const open     = expanded.has(item.path);
    const busy     = loadingPaths.has(item.path);
    const kids     = childrenMap[item.path];
    const explicit = selected.has(item.path);
    const covered  = !explicit && coveredByAncestor(item.path, selected);
    const isSel    = explicit || covered;

    return (
        <>
            <Box
                onClick={() => isDir && onSelect(item.path)}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.55, pr: 1,
                      pl: `${depth * 16 + 6}px`,
                      cursor: isDir ? "pointer" : "default",
                      bgcolor: isSel ? "#eff6ff" : "transparent",
                      borderLeft: `2px solid ${isSel ? ds.colors.primary : "transparent"}`,
                      "&:hover": { bgcolor: isDir ? (isSel ? "#eff6ff" : "#f8fafc") : "transparent" } }}>
                {isDir ? (
                    <Box component="span"
                         onClick={(e) => { e.stopPropagation(); onToggle(item.path); }}
                         sx={{ display: "flex", width: 18, flexShrink: 0, justifyContent: "center" }}>
                        {busy
                            ? <CircularProgress size={11} />
                            : <ChevronRightRoundedIcon
                                  sx={{ fontSize: 16, color: "#94a3b8", transition: "transform .12s",
                                        transform: open ? "rotate(90deg)" : "none" }} />}
                    </Box>
                ) : <Box sx={{ width: 18, flexShrink: 0 }} />}

                {/* 폴더에만 체크박스 — 여러 개를 고를 수 있다는 걸 아이콘만으로는 알 수 없다.
                    (파일은 자리만 비워 이름 시작점을 맞춘다) */}
                {isDir
                    ? <Tooltip title={covered ? "상위 폴더가 선택되어 함께 포함됩니다" : ""}
                               placement="right" disableInteractive>
                          {/* 조상이 선택된 하위는 잠근다 — 여기서 개별 해제를 허용하면
                              "상위는 켰는데 이 하나만 빼기" 라는 예외 상태가 생기고,
                              서버의 findZips 는 그런 제외를 표현할 방법이 없어 화면과 결과가 갈린다.
                              (제외는 ③ 사용자 목록의 ZIP 체크박스로 한다) */}
                          <span>
                              <Checkbox size="small" checked={isSel} disabled={covered} tabIndex={-1}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={() => onSelect(item.path)}
                                        sx={{ p: 0.25, flexShrink: 0 }} />
                          </span>
                      </Tooltip>
                    : <Box sx={{ width: 24, flexShrink: 0 }} />}

                {isDir
                    ? <FolderRoundedIcon  sx={{ fontSize: 16, color: "#f59e0b", flexShrink: 0 }} />
                    : <ArticleRoundedIcon sx={{ fontSize: 16, color: "#94a3b8", flexShrink: 0 }} />}

                <Typography noWrap sx={{ fontSize: 13, flex: 1, minWidth: 0,
                                         fontWeight: isSel ? 700 : 400,
                                         color: isDir ? "#1e293b" : "#64748b" }}>
                    {item.name}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: "#94a3b8", flexShrink: 0 }}>
                    {isDir ? "" : fmtBytes(item.size)}
                </Typography>
            </Box>

            {isDir && open && kids?.map(k => (
                <TreeNode key={k.path} item={k} depth={depth + 1}
                          childrenMap={childrenMap} expanded={expanded} loadingPaths={loadingPaths}
                          selected={selected} onToggle={onToggle} onSelect={onSelect} />
            ))}
            {isDir && open && kids?.length === 0 && (
                <Typography sx={{ fontSize: 12, color: "#cbd5e1", py: 0.4,
                                  pl: `${(depth + 1) * 16 + 30}px` }}>
                    비어 있음
                </Typography>
            )}
        </>
    );
}

/* ─── 작은 조각들 ───────────────────────────────────────── */
const Kv = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, color: "#1e293b", fontWeight: 600 }}>{value ?? "—"}</Typography>
    </Box>
);

/**
 * 미리보기 본문 — 요약 칩 + 경고 + 사람별 표.
 *
 * <p>표의 체크박스는 <b>빼는</b> 용도다. 조회 직후엔 전부 켜져 있고, 이미 넣은 사람이나
 * 매칭이 안 된 사람을 꺼서 나머지만 돌린다. 서버도 선택분만 검증하므로 매칭 실패한 사람을
 * 끄면 그대로 진행된다.
 */
function PlanView({ plan, selected, onToggle, onToggleAll }) {
    const total       = plan.people.length;
    const allSelected = total > 0 && selected.size === total;
    const someSelected = selected.size > 0 && !allSelected;

    return (
        <>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Kv label="사람(ZIP)"   value={`${plan.zipCount}명`} />
                <Kv label="메일"        value={`${fmtNum(plan.emlCount)}건`} />
                <Kv label="압축 용량"   value={fmtBytes(plan.zipBytes)} />
                <Kv label="시스템 폴더" value={`${fmtNum(plan.systemFolderMails)}건`} />
                <Kv label="내 폴더(자동 생성)" value={`${fmtNum(plan.customFolderMails)}건`} />
                <Chip size="small"
                      label={plan.unmatchedCount === 0
                          ? `직원 매칭 ${plan.matchedCount}/${plan.zipCount} 완료`
                          : `매칭 실패 ${plan.unmatchedCount}명`}
                      color={plan.unmatchedCount === 0 ? "success" : "error"} />
                {/* 위의 '메일'은 <b>대상으로 인식된 수</b>지 ZIP 안의 파일 수가 아니다.
                    그 차이를 안 보여 주면 "N건 완료" 를 보고도 원래 몇 건이었는지 알 수 없다.
                    부산물은 정상이라 회색, 구조 미인식은 사람이 봐야 해서 빨강으로 가른다. */}
                {plan.excludedArtifacts > 0 && (
                    <Chip size="small" variant="outlined"
                          label={`부산물 제외 ${fmtNum(plan.excludedArtifacts)}건`} />
                )}
                {plan.excludedUnreadable > 0 && (
                    <Chip size="small" color="error"
                          label={`구조 미인식 ${fmtNum(plan.excludedUnreadable)}건 — 확인 필요`} />
                )}
                {/* 일부만 고른 상태는 눈에 띄어야 한다 — 표를 스크롤해 내려가면 무엇을 뺐는지 안 보인다. */}
                {!allSelected && (
                    <Chip size="small" color="primary" variant="outlined"
                          label={`이관 대상 ${selected.size}/${total}명 선택`} />
                )}
            </Stack>

            {plan.warnings?.length > 0 && (
                <Box sx={{ bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1, p: 1.25, mb: 1.5 }}>
                    <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 0.5 }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 16, color: "#d97706" }} />
                        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#92400e" }}>
                            확인 필요 {plan.warnings.length}건
                        </Typography>
                    </Stack>
                    {plan.warnings.map((w, i) => (
                        <Typography key={i} sx={{ fontSize: 12, color: "#92400e" }}>· {w}</Typography>
                    ))}
                </Box>
            )}

            <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" sx={{ bgcolor: "#f8fafc" }}>
                                <Checkbox
                                    size="small"
                                    checked={allSelected}
                                    indeterminate={someSelected}
                                    onChange={onToggleAll}
                                />
                            </TableCell>
                            {["이름", "부서", "계정", "매칭된 직원", "메일", "메일함 → 들어갈 자리"].map(h => (
                                <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700, bgcolor: "#f8fafc" }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {plan.people.map(p => {
                            const checked = selected.has(p.zipName);
                            return (
                            // 체크 해제한 줄은 흐리게 — 표가 길어 스크롤하면 무엇을 뺐는지 잊는다.
                            <TableRow key={p.zipPath} hover selected={checked}
                                      sx={{ opacity: checked ? 1 : 0.45 }}>
                                <TableCell padding="checkbox">
                                    <Checkbox size="small" checked={checked}
                                              onChange={() => onToggle(p.zipName)} />
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5 }}>{p.personName || "—"}</TableCell>
                                <TableCell sx={{ fontSize: 12.5, color: "#64748b" }}>{p.department || "—"}</TableCell>
                                <TableCell sx={{ fontSize: 12.5, fontFamily: "monospace" }}>
                                    {/* 계정이 여럿인 공용 ZIP 은 전부 보여 준다 — 한 줄로 접으면
                                        "그중 하나"만 보이고 나머지가 어디로 가는지 알 수 없다. */}
                                    {(p.accounts?.length ? p.accounts : [{ account: p.account }])
                                        .map(a => a.account || "—").join(", ")}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5 }}>
                                    {p.accounts?.length > 1 ? (
                                        <Stack spacing={0.3}>
                                            {p.accounts.map(a => (
                                                <Chip key={a.account} size="small"
                                                      color={a.employeeId ? "success" : "error"}
                                                      variant={a.employeeId ? "outlined" : "filled"}
                                                      label={a.employeeId
                                                          ? `${a.account} → ${a.employeeName} (${fmtNum(a.emlCount)})`
                                                          : `${a.account} → 주인 없음 (${fmtNum(a.emlCount)})`} />
                                            ))}
                                        </Stack>
                                    ) : isPlanMatched(p)
                                        ? <Chip size="small" color="success" variant="outlined" label={p.employeeName} />
                                        : <Chip size="small" color="error" label="매칭 실패" />}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12.5, textAlign: "right" }}>{fmtNum(p.emlCount)}</TableCell>
                                <TableCell>
                                    {p.mailboxes.map(m => (
                                        <Box key={m.mailboxPath}
                                             sx={{ display: "flex", gap: 0.7, alignItems: "center", mb: 0.2 }}>
                                            <Typography sx={{ fontSize: 11.5, color: "#64748b", minWidth: 44,
                                                              textAlign: "right" }}>
                                                {fmtNum(m.count)}
                                            </Typography>
                                            <Typography noWrap sx={{ fontSize: 11.5, color: "#334155", maxWidth: 240 }}>
                                                {m.mailboxPath}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>→</Typography>
                                            <Typography noWrap sx={{ fontSize: 11.5, fontWeight: 600,
                                                              color: m.custom ? "#b45309" : ds.colors.primary }}>
                                                {m.target}
                                            </Typography>
                                        </Box>
                                    ))}
                                </TableCell>
                            </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        </>
    );
}

function JobView({ job }) {
    const stateColor = { RUNNING: "info", DONE: "success", CANCELLED: "warning", FAILED: "error" }[job.state];
    return (
        <>
            <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" color={stateColor}
                      label={{ RUNNING: "진행 중", DONE: "완료", CANCELLED: "중단됨", FAILED: "실패" }[job.state]} />
                <Kv label="진행"     value={`${fmtNum(job.processedMails)} / ${fmtNum(job.totalMails)}건 (${job.percent}%)`} />
                <Kv label="사람"     value={`${job.donePeople} / ${job.totalPeople}명`} />
                <Kv label="신규"     value={fmtNum(job.created)} />
                <Kv label="중복(건너뜀)" value={fmtNum(job.duplicated)} />
                <Kv label="실패"     value={fmtNum(job.failed)} />
                <Kv label="읽은 용량" value={fmtBytes(job.bytesRead)} />
                {job.running && <Kv label="남은 시간" value={fmtEta(job.etaSeconds)} />}
            </Stack>

            <LinearProgress variant="determinate" value={job.percent} sx={{ height: 8, borderRadius: 99, mb: 1 }} />

            {job.running && (
                <Typography sx={{ fontSize: 12.5, color: "#64748b", mb: 1 }}>
                    처리 중 — <b>{job.currentPerson || "…"}</b>
                    {job.currentMailbox ? ` › ${job.currentMailbox}` : ""}
                </Typography>
            )}
            {job.error && (
                <Typography sx={{ fontSize: 12.5, color: "#dc2626", mb: 1 }}>{job.error}</Typography>
            )}

            {job.people?.length > 0 && (
                <>
                    <Divider sx={{ my: 1.5 }} />
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {["직원", "계정", "진행", "신규", "중복", "실패"].map(h => (
                                    <TableCell key={h} sx={{ fontSize: 12, fontWeight: 700 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {job.people.map(p => (
                                <TableRow key={p.zipName}>
                                    <TableCell sx={{ fontSize: 12.5 }}>
                                        {p.done && <CheckCircleRoundedIcon sx={{ fontSize: 14, color: "#16a34a", mr: 0.5 }} />}
                                        {p.employeeName}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12.5, fontFamily: "monospace" }}>{p.account}</TableCell>
                                    <TableCell sx={{ fontSize: 12.5 }}>
                                        {fmtNum(p.created + p.duplicated + p.failed)} / {fmtNum(p.total)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12.5 }}>{fmtNum(p.created)}</TableCell>
                                    <TableCell sx={{ fontSize: 12.5, color: "#94a3b8" }}>{fmtNum(p.duplicated)}</TableCell>
                                    <TableCell sx={{ fontSize: 12.5, color: p.failed ? "#dc2626" : "#94a3b8" }}>
                                        {fmtNum(p.failed)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}

            {/* 자동 복구된 일 — 잃은 건 없다. 예전엔 이것도 '실패' 목록에 섞여 있어서
                스트림이 한 번 끊길 때마다 실패 건수가 올라가는 것처럼 보였다. */}
            {job.warnings?.length > 0 && (
                <Box sx={{ mt: 1.5, bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1,
                           p: 1.25, maxHeight: 140, overflowY: "auto" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#92400e", mb: 0.5 }}>
                        자동 복구 {job.warnings.length}건 — 유실 없음
                    </Typography>
                    {job.warnings.map((w, i) => (
                        <Typography key={i} sx={{ fontSize: 11.5, color: "#92400e" }}>· {w}</Typography>
                    ))}
                </Box>
            )}

            {job.errors?.length > 0 && (
                <Box sx={{ mt: 1.5, bgcolor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 1,
                           p: 1.25, maxHeight: 180, overflowY: "auto" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#991b1b", mb: 0.5 }}>
                        실패 {job.errors.length}건
                    </Typography>
                    {job.errors.map((e, i) => (
                        <Typography key={i} sx={{ fontSize: 11.5, color: "#991b1b" }}>· {e}</Typography>
                    ))}
                </Box>
            )}
        </>
    );
}
